import React from 'react';
import { useField } from 'formik';
import { useTranslation } from '@blocs.i18n';
import {
  getHelpTextValue,
  normalizeFormikError,
  WrappedBaseInput,
  WrappedBaseInputProps,
  BaseInput,
} from './BaseInput';

/**
 * A basic text input component with label, help text, and validation but unconnected to Formik state
 * so you'll need to pass the value, eventHandlers, and validation props manually
 * @name WrappedUnconnectedTextInput
 * @example
 * <WrappedUnconnectedTextInput
 *   inputProps={{
 *     name: 'fieldName',
 *     disabled: isDisabled,
 *     value: fieldValue,
 *     onChange: (newVal) => setFieldValue(newVal),
 *   }}
 *   wrapperProps={{ id: 'fieldName', label: 'Field Name' }}
 * />
 */
export const WrappedUnconnectedTextInput = WrappedBaseInput;

type TextLikeInputTypes = 'text' | 'password' | 'number' | 'search' | 'email' | 'tel' | 'url';

export type ConnectedTextInputProps = {
  /** name of the field in the formik state */
  name: string;
  /** is the field disabled */
  disabled?: boolean;
  /** label for the input */
  label?: string;
  /** help text to display below the input */
  helpText?: string;
  /** specify a type of text input, defaults to "text" */
  type?: TextLikeInputTypes;
  /** props passed directly through to the underlying input, you should not need this in most cases */
  inputProps?: Omit<
    WrappedBaseInputProps['inputProps'],
    'invalid' | 'onChange' | 'onBlur' | 'value' | 'type' | 'id' | 'name'
  >;
  /** props passed directly through to the <FieldWrapper /> component, you should not need this in most cases */
  wrapperProps?: Omit<
    WrappedBaseInputProps['wrapperProps'],
    'id' | 'label' | 'helpText' | 'disabled' | 'validation' | 'tooltip' | 'helpTextAbove'
  >;
  /** tooltip to display next to the label */
  tooltip?: React.ReactNode;
  /** help text to display above the input */
  helpTextAbove?: string;
  /** prevents the user from entering a value longer than maxLength
   * also sets helpText below the input if maxLength is reached to `${maxLength} Character limit reached` */
  maxLength?: number;
  /** transforms the typed value before it is written to Formik */
  formatValue?: (value: string) => string;
  /** minimum value for number inputs */
  min?: number;
  /** maximum value for number inputs */
  max?: number;
  /** marks the field as required: appends " *" to the label, sets aria-required, and defers the
   * "required" validation error until the field has been touched, so an untouched field the user
   * hasn't reached yet is not reported as invalid */
  required?: boolean;
};

/**
 * A text input tied into the a Formik Field's state
 * @name TextInput
 * @description this should be used as the normal text input field for all Formik forms.
 * See BaseInput for more details of inputProps and wrapperProps, though you should not need them in most cases
 *
 * @example
 * <TextInput name="input" label="My Input" /> // most basic
 * <TextInput name="input" label="My Input" type="email" /> // custom input type to pass to underlying <input />
 * <TextInput name="input" label="My Input" helpText="Please Fill This Out" /> // with help text
 * <TextInput name="input" label="My Input" disabled={isDisabled} /> // disabled
 */
export const TextInput = ({
  name,
  disabled = false,
  label,
  helpText: helpTextProp,
  wrapperProps,
  inputProps,
  type = 'text',
  tooltip,
  helpTextAbove,
  maxLength,
  min,
  max,
  required = false,
  formatValue,
}: ConnectedTextInputProps) => {
  const { t } = useTranslation();
  const [input, meta, helpers] = useField<string>(name);
  const { value: formikValue, onBlur } = input;
  const { error, touched } = meta;
  const hasMaxLength = typeof maxLength === 'number';
  const helpText = getHelpTextValue({ maxLength, helpTextProp, currentValue: formikValue, t });

  const normalizedType = ['text', 'password', 'number', 'search', 'email', 'tel', 'url'].includes(type) ? type : 'text';
  const isInvalid = !!error && (!required || !!touched);
  const fieldValidation = isInvalid ? { invalid: true } : {};
  const wrapperValidation = isInvalid ? { validation: { valid: false, message: normalizeFormikError(error) } } : {};

  const onChange = (newVal: string) => {
    let valToSet = formatValue ? formatValue(newVal) : newVal;
    if (hasMaxLength && valToSet?.length >= maxLength) {
      valToSet = valToSet.slice(0, maxLength);
    }
    helpers.setValue(valToSet);
  };

  // this prevents the console warning about switching between
  // controlled and uncontrolled input types
  let inputValue = formikValue;

  if (inputValue === undefined || inputValue === null) {
    inputValue = '';
  }

  return (
    <WrappedBaseInput
      wrapperProps={{
        label: label && required ? `${label} *` : label,
        helpText,
        ...wrapperProps,
        ...wrapperValidation,
        id: name,
        disabled,
        tooltip,
        helpTextAbove,
      }}
      inputProps={{
        ...inputProps,
        ...fieldValidation,
        'aria-required': required || undefined,
        'aria-invalid': isInvalid || undefined,
        value: inputValue,
        name,
        id: name,
        onChange,
        onBlur,
        disabled,
        type: normalizedType,
        min: normalizedType === 'number' && typeof min === 'number' ? min : undefined,
        max: normalizedType === 'number' && typeof max === 'number' ? max : undefined,
      }}
    />
  );
};

export type UnwrappedConnectedTextInputProps = {
  /** name of the field in the formik state */
  name: string;
  /** is the field disabled */
  disabled?: boolean;
  /** label for the input for screen readers */
  'aria-label': string;
  /** specify a type of text input, defaults to "text" */
  type?: TextLikeInputTypes;
  /** props passed directly through to the underlying input, you should not need this in most cases */
  inputProps?: Omit<WrappedBaseInputProps['inputProps'], 'invalid' | 'onChange' | 'value' | 'type' | 'id' | 'name'>;
  /** minimum value for number inputs */
  min?: number;
  /** maximum value for number inputs */
  max?: number;
};

/**
 * A text input tied into the a Formik Field's state but without a visible label, help text or validation message
 * @name UnwrappedTextInput
 * @description this should only be used in rare cases where you do not want a visible label, help text or validation message
 * See BaseInput for more details of inputProps, though you should not need them in most cases
 *
 * @example
 * <UnwrappedTextInput name="input" aria-label="My Input" /> // most basic
 * <UnwrappedTextInput name="input" aria-label="My Input" type="email" /> // custom input type to pass to underlying <input />
 * <UnwrappedTextInput name="input" aria-label="My Input" type="number" min={1} max={10} /> // min and max for number input
 * <UnwrappedTextInput name="input" aria-label="My Input" disabled={isDisabled} /> // disabled
 */
export const UnwrappedTextInput = ({
  name,
  disabled = false,
  'aria-label': label,
  inputProps,
  type = 'text',
  min,
  max,
}: UnwrappedConnectedTextInputProps) => {
  const [input, meta, helpers] = useField<string>(name);
  const { value: formikValue, onBlur: formikOnBlur } = input;
  const { error } = meta;

  const normalizedType = ['text', 'password', 'number', 'search', 'email', 'tel', 'url'].includes(type) ? type : 'text';
  const hasError = !!error;
  const fieldValidation = hasError ? { invalid: true } : {};

  const onChange = (newVal: string) => {
    helpers.setValue(newVal);
  };

  // Merge Formik's onBlur with any custom onBlur from inputProps
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    formikOnBlur(e);
    inputProps?.onBlur?.(e);
  };

  // this prevents the console warning about switching between
  // controlled and uncontrolled input types
  let inputValue = formikValue;

  if (inputValue === undefined || inputValue === null) {
    inputValue = '';
  }

  return (
    <BaseInput
      {...inputProps}
      {...fieldValidation}
      value={inputValue}
      name={name}
      aria-label={label}
      onChange={onChange}
      onBlur={handleBlur}
      disabled={disabled}
      type={normalizedType}
      min={normalizedType === 'number' && typeof min === 'number' ? min : undefined}
      max={normalizedType === 'number' && typeof max === 'number' ? max : undefined}
    />
  );
};
